import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Pie } from 'react-chartjs-2';
import { DataContext } from '../contexts/DataContext';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const ShipmentDefaultUqPieChart = () => {
  const { shipments, isLoading } = useContext(DataContext);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
        <span className="ml-2 text-lg">Loading...</span>
      </div>
    );
  }

  if (!shipments) {
    return <div>No data available</div>;
  }

  // Counting shipments by 'defaultUq' values
  const defaultUqCounts = shipments.reduce((acc, shipment) => {
    const defaultUq = shipment.defaultUq || "Unknown"; // Group shipments without 'defaultUq' under "Unknown"
    acc[defaultUq] = (acc[defaultUq] || 0) + 1;
    return acc;
  }, {});

  const defaultUqLabels = Object.keys(defaultUqCounts);
  const defaultUqData = Object.values(defaultUqCounts);

  const backgroundColors = [
    'rgba(25, 99, 201, 0.8)',  // Dark blue with some transparency
    'rgba(21, 86, 175, 0.8)',  // Slightly darker blue
    'rgba(17, 73, 149, 0.8)',  // Even darker blue
    'rgba(13, 60, 123, 0.8)',  // Nearing navy blue
    'rgba(10, 50, 100, 0.8)'   // Deep navy blue
  ];
  
  const borderColors = [
    'rgba(25, 99, 201, 1)',  // Solid dark blue
    'rgba(21, 86, 175, 1)',  // Solid slightly darker blue
    'rgba(17, 73, 149, 1)',  // Solid even darker blue
    'rgba(13, 60, 123, 1)',  // Solid nearing navy blue
    'rgba(10, 50, 100, 1)'   // Solid deep navy blue
  ];
  
  

  const chartData = {
    labels: defaultUqLabels,
    datasets: [
      {
        label: 'Shipments by Default UQ',
        data: defaultUqData,
        backgroundColor: [
          '#4B77BE', // Deep blue
          '#5CACEE', // Light blue
          '#77B1A9', // Greenish blue
          '#72C6EF', // Sky blue
          '#8E44AD', // Purple
          '#3498DB', // Blue
          '#2ECC71', // Green
          '#E74C3C'  // Red
        ],
        hoverBackgroundColor: [
          '#354A5F', // Darker deep blue
          '#3E6B8E', // Darker light blue
          '#5D8A76', // Darker greenish blue
          '#5DA0C3', // Darker sky blue
          '#6C3483', // Darker purple
          '#2980B9', // Darker blue
          '#27AE60', // Darker green
          '#C0392B'  // Darker red
        ],
      },
    ],
  };
  

  const options = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: '#f8f8f8', // Near white color for labels inside the slices
        font: {
          size: 12, // Smaller font size for better fitting
          family: 'Inter, sans-serif'
        },
        formatter: (value) => `${value}`, // Only display the quantity
      },
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
            family: 'Inter, sans-serif',
            weight: '400',
            color: '#333'
          },
          boxWidth: 20,
          padding: 20,
        },
      },
      tooltip: {
        bodyFont: {
          size: 14,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        titleFont: {
          size: 16,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        backgroundColor: '#ffffff',
        borderColor: '#cccccc',
        borderWidth: 1,
        titleColor: '#333',
        bodyColor: '#333'
      }
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartPie} className="text-blue-600 mr-2" />
          Default UQ Distribution
        </h2>
      </div>
      <div className="flex-grow relative">
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
  
  
};

export default ShipmentDefaultUqPieChart;
